import { useDisclosure } from '@mantine/hooks';
import clsx from 'clsx';
import { cloneElement, useMemo, useRef } from 'react';
import { useHoverDirty } from 'react-use';
import { useSiteTheme } from 'src/contexts/SiteTheme/SiteThemeContext';
import { vars } from 'src/core/themes';
import { Stack, Tooltip } from 'src/core/ui';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { IconsFill } from 'src/svgs/Viagogo';
import { Feature, ListingNoteType } from 'src/WebApiController';

import * as styles from './ListingNoteBadge.css';

export const ListingNoteBadge = ({
  listingNotes,
  icon,
  type,
  triggerContentClassName,
  mobile,
}: {
  listingNotes?: string[];
  icon: JSX.Element;
  type: ListingNoteType;
  triggerContentClassName?: string;
  mobile?: boolean;
}) => {
  const { isDarkMode } = useSiteTheme();
  const ref = useRef<HTMLDivElement>(null);
  const isHovering = useHoverDirty(ref);
  const [open, { toggle }] = useDisclosure(false);
  const hasUnitCostInBulkPrevFeature = useUserHasFeature(
    Feature.UnitCostInBulkPreview
  );

  const styledIcon = cloneElement(icon, {
    size: hasUnitCostInBulkPrevFeature ? vars.iconSize.s : vars.iconSize.m,
    fill:
      type === ListingNoteType.Defect
        ? IconsFill.textWarning
        : IconsFill.textPrimary,
  });

  const content = (
    <Stack
      ref={ref}
      alignItems="center"
      onClick={() => mobile && toggle()}
      className={clsx(
        hasUnitCostInBulkPrevFeature ? styles.listingNoteBdge : '',
        triggerContentClassName
      )}
    >
      {styledIcon}
    </Stack>
  );

  const sanitizedOpen = useMemo(() => {
    if (mobile) {
      return open;
    }
    return isHovering;
  }, [isHovering, mobile, open]);

  return listingNotes ? (
    <Tooltip
      open={sanitizedOpen}
      onOpenChange={(_) => mobile && toggle()}
      triggerContent={content}
      contentVariant={isDarkMode ? 'light' : 'darkGray'}
    >
      <Stack direction="column" style={{ padding: vars.spacing['sm'] }}>
        {listingNotes?.map((note, idx) => <span key={idx}>{`• ${note}`}</span>)}
      </Stack>
    </Tooltip>
  ) : null;
};
